import { defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { CallCreateResponse, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { ERRORS, EVENTS } from "@/const";
import PaidActionConfirm from "@/components/PaidActionConfirm";
import PaidActionError from "@/components/PaidActionError";
import CallModalView from "./components/CallModalView.vue";
import CallNotification from "./components/CallNotification";
import useCalls from "./hooks/useCalls";
import usePaidCalls from "./hooks/usePaidCalls";

export default defineComponent({
  name: "CallController",
  components: { PaidActionError, PaidActionConfirm, CallModalView, CallNotification },
  setup() {
    const { authUserStrict, isAdmin } = useAuthUser();
    const forceSmallView = ref(false);

    const {
      opponentUser,
      showNotification,
      call,
      loading,
      opponentStream,
      localStream,
      localReadyVideoState,
      opponentReadyVideoState,
      showConnectionError,
      connectionErrorMessage,
      localVideoState,
      opponentVideoState,
      connectingState,
      opponentSubscriptionStatus,
      setVideoState,
      callOutgoingHandler,
      callIncomingHandler,
      handleCallBack,
      onChangeDevice,
      handleCallAccept,
      callUpdateHandler,
      collectErrorLogs,
      init: initCalls,
      reset: resetCall,
    } = useCalls();

    let { showPaidConfirm, showRefillBalance, callPrice, getCallPrice, reset: resetPaidCall } = usePaidCalls(call);

    const reset = async (immediate: boolean = false, ignore: boolean = false) => {
      await resetCall(immediate, ignore);
      resetPaidCall();
    };

    const setPaidChatAnswer = async (status: boolean) => {
      showPaidConfirm.value = false;
      if (status && opponentUser.value) {
        try {
          providers.PaidCallsProvider.setReceiveErrors(false);
          await callOutgoingHandler({ opponentUser: opponentUser.value });
        } catch (e) {
          handleRequestError(e, [
            {
              comparator: e => e.details?.messageTemplate === ERRORS.PAID_CALL_CREATE,
              onSuccessCB: () => (showRefillBalance.value = true),
            },
          ]);
        }
      } else {
        reset(true, true);
      }
    };

    const onPaidErrorAnswer = (v: boolean) => {
      showRefillBalance.value = v;
      if (call.value && ![CallStatus.NEW, CallStatus.ACCEPTED].includes(call.value?.status)) {
        reset(true, false);
      }
    };

    const paidCallOutgoingHandler = async (data: { subject?: Appointment; opponentUser: UserPublicData }) => {
      const isFreeCall = !!data.subject;
      if (call.value) return;
      await getCallPrice(data.opponentUser.id);
      if (callPrice.value === 0 || isAdmin.value || isFreeCall) {
        try {
          await callOutgoingHandler(data);
          // eslint-disable-next-line
        } catch (e) {}
      } else {
        opponentUser.value = data.opponentUser;
        showPaidConfirm.value = true;
      }
    };

    const paidCallIncomingHandler = async (callCreateResponse: CallCreateResponse) => {
      if (call.value) return;
      forceSmallView.value = true;
      await getCallPrice(authUserStrict.value.id);
      await callIncomingHandler(callCreateResponse);
    };

    let unsubscribe: () => void | undefined;

    const init = async () => {
      initCalls();
      emitter.on<any>(EVENTS.CALL_OUTGOING, paidCallOutgoingHandler);
      loading.value = true;
      await Promise.all([
        providers.CallsProvider.subscribeIncomingCall(paidCallIncomingHandler),
        providers.CallsProvider.subscribeCallStatusUpdated(callUpdateHandler),
        providers.CallsProvider.findActive(),
      ])
        .then(([subscriptionIncomingCall, subscriptionStatusUpdate, calls]) => {
          if (calls.length) callIncomingHandler(calls[0]);
          unsubscribe = () => {
            if (subscriptionIncomingCall) subscriptionIncomingCall.unsubscribe();
            if (subscriptionStatusUpdate) subscriptionStatusUpdate.unsubscribe();
          };
        })
        .catch(error => {
          collectErrorLogs("Created get initial data", error);
        });
      loading.value = true;
    };

    onBeforeUnmount(() => {
      unsubscribe && unsubscribe();
      emitter.off<any>(EVENTS.CALL_OUTGOING, paidCallOutgoingHandler);
    });

    init();

    return {
      opponentUser,
      showNotification,
      showPaidConfirm,
      showRefillBalance,
      call,
      callPrice,
      loading,
      opponentStream,
      localStream,
      localReadyVideoState,
      opponentReadyVideoState,
      showConnectionError,
      connectionErrorMessage,
      localVideoState,
      opponentVideoState,
      connectingState,
      opponentSubscriptionStatus,
      forceSmallView,
      setVideoState,
      reset,
      handleCallBack,
      onChangeDevice,
      handleCallAccept,
      setPaidChatAnswer,
      onPaidErrorAnswer,
    };
  },
});
